import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Play,
  Pause,
  Download,
  Clock,
  Bot,
  User,
  ArrowRightLeft,
  CheckCircle,
  XCircle,
  Filter
} from 'lucide-react';
import { voiceService, VoiceCallLog, VoiceCallStats } from '@/services/voice.service';
import { Pagination } from '@centraoneplus/ui';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { ThemedSelect } from '@components/common/ThemedSelect';
import { useAuthStore } from '@stores/authStore';
import { settingsService, type User as StaffUser } from '@/services/settings.service';

/** Roles that see every agent's calls (the server enforces the same rule). */
const SEE_ALL_CALLS = new Set(['owner', 'admin', 'supervisor']);

/** A person's name as the console shows it. */
function nameOf(u: Pick<StaffUser, 'displayName' | 'firstName' | 'lastName' | 'email'>): string {
  return u.displayName?.trim() || [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email;
}

interface VoiceCallHistoryProps {
  onCallSelect?: (call: VoiceCallLog) => void;
}

/** The floating shell, same card as every other surface on the console. */
const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

const SELECT =
  'h-[34px] rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 ' +
  'text-[12.5px] text-[var(--co-ink-2)] focus:border-[var(--co-brass)] focus:outline-none';

export function VoiceCallHistory({ onCallSelect }: VoiceCallHistoryProps) {
  const [calls, setCalls] = useState<VoiceCallLog[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<VoiceCallStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [dateRange, setDateRange] = useState<'today' | '7days' | '30days'>('7days');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Whose calls: an agent sees only their own; an admin sees everyone's and
  // can narrow it to one agent.
  const role = useAuthStore((s) => s.user?.role);
  const seesAll = SEE_ALL_CALLS.has(String(role || '').toLowerCase());
  const [agentFilter, setAgentFilter] = useState<string>('all');
  const [agents, setAgents] = useState<StaffUser[]>([]);
  useEffect(() => {
    if (!seesAll) return;
    settingsService
      .getUsers({ limit: 200 })
      .then((page) => setAgents(page.data.filter((u) => u.status === 'active')))
      .catch(() => setAgents([]));
  }, [seesAll]);
  const agentId = seesAll && agentFilter !== 'all' ? agentFilter : undefined;

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Audio player
  const [playingCallSid, setPlayingCallSid] = useState<string | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  // Recordings kept in our own store are fetched with the agent's session and
  // played from memory, so the browser is never sent to the phone provider.
  const [fetchingCallSid, setFetchingCallSid] = useState<string | null>(null);
  const [noRecording, setNoRecording] = useState<Set<string>>(new Set());
  const recordingUrls = useRef(new Map<string, string>());
  useEffect(() => {
    const urls = recordingUrls.current;
    return () => {
      for (const url of urls.values()) URL.revokeObjectURL(url);
      urls.clear();
    };
  }, []);

  /** The audio for a call: its provider URL (Twilio), else fetched from our store. */
  const recordingSource = async (call: VoiceCallLog): Promise<string | null> => {
    if (call.recordingUrl) return call.recordingUrl;
    const held = recordingUrls.current.get(call.callSid);
    if (held) return held;
    setFetchingCallSid(call.callSid);
    try {
      const url = URL.createObjectURL(await voiceService.getCallRecording(call.callSid));
      recordingUrls.current.set(call.callSid, url);
      return url;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setNoRecording((prev) => new Set(prev).add(call.callSid));
      } else {
        setError('The recording could not be loaded. Try again in a moment.');
      }
      return null;
    } finally {
      setFetchingCallSid(null);
    }
  };

  const downloadRecording = async (call: VoiceCallLog) => {
    const url = await recordingSource(call);
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = `call_recording_${call.callSid}`;
    link.click();
  };

  const getDateRange = useCallback(() => {
    const now = new Date();
    switch (dateRange) {
      case 'today':
        return { from: startOfDay(now), to: endOfDay(now) };
      case '7days':
        return { from: startOfDay(subDays(now, 7)), to: endOfDay(now) };
      case '30days':
        return { from: startOfDay(subDays(now, 30)), to: endOfDay(now) };
      default:
        return { from: startOfDay(subDays(now, 7)), to: endOfDay(now) };
    }
  }, [dateRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = getDateRange();

      const [logs, statsData] = await Promise.all([
        voiceService.getCallLogs({
          from: from.toISOString(),
          to: to.toISOString(),
          status: statusFilter !== 'all' ? statusFilter : undefined,
          limit: pageSize,
          offset: (page - 1) * pageSize,
          agentId,
        }),
        voiceService.getCallStats(from.toISOString(), to.toISOString(), agentId),
      ]);

      setCalls(logs.data);
      setTotal(logs.total);
      setStats(statsData);
      setError(null);
    } catch (err) {
      setError('Failed to load call history');
      console.error('Error fetching call history:', err);
    } finally {
      setLoading(false);
    }
  }, [getDateRange, statusFilter, page, pageSize, agentId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 whenever the filter set changes — staying on page 5 of
  // the previous result set produces an empty page.
  useEffect(() => {
    setPage(1);
  }, [dateRange, statusFilter, agentFilter]);

  const playRecording = async (call: VoiceCallLog) => {
    if (playingCallSid === call.callSid) {
      // Pause current
      audioElement?.pause();
      setPlayingCallSid(null);
      return;
    }
    // Stop previous
    audioElement?.pause();
    setPlayingCallSid(null);

    const source = await recordingSource(call);
    if (!source) return;

    // Play new
    const audio = new Audio(source);
    audio.onended = () => setPlayingCallSid(null);
    audio.play().catch(() => setPlayingCallSid(null));

    setAudioElement(audio);
    setPlayingCallSid(call.callSid);
  };

  /** A recording to offer: the provider's own URL, or one our store holds or can fetch. */
  const hasRecording = (call: VoiceCallLog) =>
    Boolean(call.recordingUrl) ||
    ((call.recording === 'available' || call.recording === 'check') && !noRecording.has(call.callSid));

  const getDirectionIcon = (direction: string) => {
    return direction === 'inbound' ? (
      <PhoneIncoming className="h-4 w-4" style={{ color: 'var(--co-machine-ink)' }} />
    ) : (
      <PhoneOutgoing className="h-4 w-4" style={{ color: 'var(--co-green-ink)' }} />
    );
  };

  const getOutcomeIcon = (call: VoiceCallLog) => {
    if (call.endReason === 'ai_resolved') {
      return <Bot className="h-4 w-4" style={{ color: 'var(--co-machine-ink)' }} />;
    } else if (call.endReason === 'handoff') {
      return <ArrowRightLeft className="h-4 w-4" style={{ color: 'var(--co-terracotta-ink)' }} />;
    } else if (call.status === 'completed') {
      return <CheckCircle className="h-4 w-4" style={{ color: 'var(--co-green-ink)' }} />;
    } else {
      return <XCircle className="h-4 w-4" style={{ color: 'var(--co-burgundy)' }} />;
    }
  };

  const getOutcomeLabel = (call: VoiceCallLog) => {
    if (call.endReason === 'ai_resolved') return 'AI Resolved';
    if (call.endReason === 'handoff') return 'Transferred';
    if (call.endReason === 'customer_hangup') return 'Customer Hangup';
    if (call.endReason === 'agent_hangup') return 'Agent Hangup';
    if (call.status === 'completed') return 'Completed';
    return call.status;
  };

  return (
    <div className={`${CARD_CLASS} flex-none overflow-hidden`} style={CARD}>
      {/* Header with Stats */}
      <div className="border-b border-[var(--co-line)] px-5 py-[16px]">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-[14px] font-bold text-[var(--co-ink)]">
            <Phone className="h-[17px] w-[17px] text-[var(--co-ink-3)]" />
            {seesAll ? 'Call History' : 'Your Calls'}
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            {seesAll && (
              <ThemedSelect
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
                className={SELECT}
                aria-label="Agent"
              >
                <option value="all">All agents</option>
                {agents.map((u) => (
                  <option key={u.id} value={u.id}>
                    {nameOf(u)}
                  </option>
                ))}
              </ThemedSelect>
            )}
            <ThemedSelect
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as any)}
              className={SELECT}
            >
              <option value="today">Today</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
            </ThemedSelect>
            <ThemedSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={SELECT}
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="no-answer">No Answer</option>
            </ThemedSelect>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 gap-[14px] lg:grid-cols-4">
            <HistoryFigure label="Total Calls" value={stats.totalCalls} accent="neutral" />
            <HistoryFigure label="AI Resolved" value={stats.aiResolvedCalls} accent="machine" />
            <HistoryFigure label="Handoffs" value={stats.handoffCalls} accent="terracotta" />
            <HistoryFigure
              label="AI Resolution Rate"
              value={`${Math.round(stats.aiResolutionRate * 100)}%`}
              accent="green"
            />
          </div>
        )}
      </div>

      {/* Call List */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-[12.5px] text-[var(--co-ink-3)]">
            Loading call history...
          </div>
        ) : error ? (
          <div className="p-10 text-center text-[12.5px]" style={{ color: 'var(--co-burgundy)' }}>
            {error}
          </div>
        ) : calls.length === 0 ? (
          <div className="p-10 text-center text-[12.5px] text-[var(--co-ink-3)]">
            <Phone className="mx-auto mb-3 h-10 w-10 text-[var(--co-line-strong)]" />
            <p>No calls found for the selected period</p>
          </div>
        ) : (
          <table className="min-w-full">
            <thead className="bg-[var(--co-sunken)]">
              <tr>
                {[
                  'Direction',
                  'Caller',
                  'Date/Time',
                  'Duration',
                  'AI Messages',
                  'Outcome',
                  'Handled by',
                  'Ticket',
                  'Recording',
                ].map((h) => (
                  <th
                    key={h}
                    className="co-mono px-4 py-3 text-left text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--co-line-subtle)]">
              {calls.map((call) => (
                <tr
                  key={call.callSid}
                  className="cursor-pointer transition-colors hover:bg-[var(--co-selected)]"
                  onClick={() => onCallSelect?.(call)}
                >
                  <td className="whitespace-nowrap px-4 py-3">{getDirectionIcon(call.direction)}</td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <div className="co-mono text-[12.5px] font-semibold text-[var(--co-ink)]">
                      {voiceService.formatPhoneNumber(call.from)}
                    </div>
                    {call.callerCity && (
                      <div className="mt-[3px] text-[11.5px] text-[var(--co-ink-3)]">
                        {call.callerCity}, {call.callerCountry}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-[12px] text-[var(--co-ink-3)]">
                    {format(new Date(call.startedAt), 'MMM d, yyyy h:mm a')}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className="flex items-center gap-1 text-[12px] text-[var(--co-ink-2)]">
                      <Clock className="h-3 w-3" />
                      {call.durationSeconds ? voiceService.formatDuration(call.durationSeconds) : '-'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className="flex items-center gap-1 text-[12px] text-[var(--co-ink-2)]">
                      <Bot className="h-3 w-3" style={{ color: 'var(--co-machine-ink)' }} />
                      {call.aiMessageCount}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className="flex items-center gap-1 text-[12px] text-[var(--co-ink-2)]">
                      {getOutcomeIcon(call)}
                      {getOutcomeLabel(call)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-[12px] text-[var(--co-ink-2)]">
                    {call.handledByName ?? <span className="text-[var(--co-ink-3)]">AI only</span>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-[12px]">
                    {call.ticket ? (
                      <a
                        href={`/tickets/${call.ticket.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="co-mono font-semibold text-[var(--co-brass-ink)] hover:underline"
                      >
                        {call.ticket.ticketNumber}
                      </a>
                    ) : (
                      <span className="text-[var(--co-ink-3)]">—</span>
                    )}
                    {call.ticket && (
                      <div className="mt-[3px] text-[11.5px] text-[var(--co-ink-3)]">
                        {call.ticket.assigneeName ? `Assigned to ${call.ticket.assigneeName}` : 'Unassigned'}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {hasRecording(call) ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void playRecording(call);
                          }}
                          disabled={fetchingCallSid === call.callSid}
                          aria-busy={fetchingCallSid === call.callSid}
                          aria-label={playingCallSid === call.callSid ? 'Pause recording' : 'Play recording'}
                          className="rounded-full border border-[var(--co-line)] bg-[var(--co-raised)] p-1.5 text-[var(--co-ink-2)] transition-colors hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] disabled:cursor-wait disabled:opacity-60"
                          title={
                            fetchingCallSid === call.callSid
                              ? 'Loading the recording…'
                              : playingCallSid === call.callSid
                                ? 'Pause'
                                : 'Play'
                          }
                        >
                          {fetchingCallSid === call.callSid ? (
                            <Clock className="h-4 w-4 animate-pulse" />
                          ) : playingCallSid === call.callSid ? (
                            <Pause className="h-4 w-4" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                        </button>
                        {call.recordingUrl ? (
                          <a
                            href={call.recordingUrl}
                            download
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-full border border-[var(--co-line)] bg-[var(--co-raised)] p-1.5 text-[var(--co-ink-2)] transition-colors hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)]"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void downloadRecording(call);
                            }}
                            disabled={fetchingCallSid === call.callSid}
                            aria-label="Download recording"
                            className="rounded-full border border-[var(--co-line)] bg-[var(--co-raised)] p-1.5 text-[var(--co-ink-2)] transition-colors hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] disabled:cursor-wait disabled:opacity-60"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="text-[12px] text-[var(--co-ink-3)]">No recording</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination — hidden when the entire result set fits on one page */}
      {!loading && !error && calls.length > 0 && total > 0 && (
        <div className="border-t border-[var(--co-line)] px-5 py-3">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        </div>
      )}
    </div>
  );
}

/** One figure above the table. Same card as the console's counters. */
function HistoryFigure({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent: 'neutral' | 'machine' | 'terracotta' | 'green';
}) {
  const ink =
    accent === 'machine'
      ? 'var(--co-machine-ink)'
      : accent === 'terracotta'
        ? 'var(--co-terracotta-ink)'
        : accent === 'green'
          ? 'var(--co-green-ink)'
          : 'var(--co-ink)';
  return (
    <div className="rounded-lg border border-[var(--co-line)] bg-[var(--co-sunken)] px-4 py-3">
      <div className="co-mono text-[22px] font-semibold leading-none tabular-nums" style={{ color: ink }}>
        {value}
      </div>
      <div className="co-mono mt-[9px] text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
        {label}
      </div>
    </div>
  );
}

export default VoiceCallHistory;
